import { useEffect, useState } from "react";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useToast } from "../context/ToastContext.jsx";
import { fechaHora } from "../utils/format.js";
import { PageHeader, Modal, Loading, EmptyState } from "../components/UI.jsx";
import { Icon } from "../components/Icons.jsx";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "gerente", label: "Gerente" },
  { value: "cajero", label: "Cajero" },
];

export default function Empleados() {
  const { esAdmin } = useAuth();
  const toast = useToast();
  const [lista, setLista] = useState(null);
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [q, setQ] = useState("");
  const [rol, setRol] = useState("");
  const [activo, setActivo] = useState("");
  // Fecha de alta del usuario. Vacio por defecto: se ve todo el personal.
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const cargar = () => {
    const params = {};
    if (q) params.q = q;
    if (rol) params.rol = rol;
    if (activo) params.activo = activo;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    api.get("/usuarios", { params }).then((r) => setLista(r.data.data));
  };
  useEffect(cargar, [q, rol, activo, desde, hasta]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm({
      nombre: "",
      email: "",
      password: "",
      rol: "cajero",
      telefono: "",
    });
    setModal(true);
  };
  const abrirEditar = (u) => {
    setEditando(u.id);
    setForm({ ...u, password: "" });
    setModal(true);
  };

  const guardar = async () => {
    if (!form.nombre || !form.email) {
      toast.error("Nombre y correo requeridos");
      return;
    }
    if (!editando && !form.password) {
      toast.error("La contrasena es requerida");
      return;
    }
    try {
      if (editando) await api.put(`/usuarios/${editando}`, form);
      else await api.post("/usuarios", form);
      toast.success("Empleado guardado");
      setModal(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const desactivar = async (u) => {
    if (!confirm(`Desactivar a ${u.nombre}?`)) return;
    try {
      await api.delete(`/usuarios/${u.id}`);
      toast.success("Empleado desactivado");
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    }
  };

  const colorRol = {
    admin: "var(--danger)",
    gerente: "var(--accent-texto)",
    cajero: "var(--info)",
  };

  return (
    <div>
      <PageHeader title="Empleados" subtitle="Usuarios con acceso al sistema">
        {esAdmin && (
          <button className="btn btn-primary" onClick={abrirNuevo}>
            <Icon.plus /> Nuevo empleado
          </button>
        )}
      </PageHeader>

      <div className="card" style={{ padding: 14, marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div className="field" style={{ margin: 0, flex: "1 1 180px" }}>
          <label>Buscar</label>
          <input className="input" placeholder="Nombre, correo, telefono…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0, minWidth: 130 }}>
          <label>Rol</label>
          <select className="select" value={rol} onChange={(e) => setRol(e.target.value)}>
            <option value="">Todos</option>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 120 }}>
          <label>Estado</label>
          <select className="select" value={activo} onChange={(e) => setActivo(e.target.value)}>
            <option value="">Todos</option>
            <option value="1">Activos</option>
            <option value="0">Inactivos</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Alta desde</label>
          <input className="input" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>Hasta</label>
          <input className="input" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <button className="btn btn-outline btn-sm" onClick={() => { setQ(""); setRol(""); setActivo(""); setDesde(""); setHasta(""); }}>
          Limpiar
        </button>
      </div>

      {!lista ? (
        <Loading />
      ) : lista.length === 0 ? (
        <EmptyState icon="empleados" titulo="Sin empleados" />
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Correo</th>
                <th>Rol</th>
                <th>Ultimo acceso</th>
                <th className="text-center">Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lista.map((u) => (
                <tr key={u.id} style={{ opacity: u.activo ? 1 : 0.5 }}>
                  <td style={{ fontWeight: 600 }}>{u.nombre}</td>
                  <td className="muted">{u.email}</td>
                  <td>
                    <span
                      className="badge"
                      style={{
                        background: "var(--accent-soft)",
                        color: colorRol[u.rol],
                        textTransform: "capitalize",
                      }}
                    >
                      {u.rol}
                    </span>
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {u.ultimo_acceso ? fechaHora(u.ultimo_acceso) : "Nunca"}
                  </td>
                  <td className="text-center">
                    {u.activo ? (
                      <span className="badge badge-success">Activo</span>
                    ) : (
                      <span className="badge badge-danger">Inactivo</span>
                    )}
                  </td>
                  <td>
                    {esAdmin && (
                      <div
                        className="flex gap-sm"
                        style={{ justifyContent: "flex-end" }}
                      >
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => abrirEditar(u)}
                        >
                          <Icon.edit width={15} height={15} />
                        </button>
                        {u.activo && (
                          <button
                            className="btn btn-sm"
                            style={{ color: "var(--danger)" }}
                            onClick={() => desactivar(u)}
                          >
                            <Icon.trash width={15} height={15} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editando ? "Editar empleado" : "Nuevo empleado"}
      >
        <div className="field">
          <label>Nombre *</label>
          <input
            className="input"
            value={form.nombre || ""}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Correo *</label>
          <input
            className="input"
            type="email"
            value={form.email || ""}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div
          className="grid-2" style={{ gap: 12 }}
        >
          <div className="field">
            <label>Rol</label>
            <select
              className="select"
              value={form.rol || "cajero"}
              onChange={(e) => setForm({ ...form, rol: e.target.value })}
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Telefono</label>
            <input
              className="input"
              value={form.telefono || ""}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
            />
          </div>
        </div>
        <div className="field">
          <label>
            {editando
              ? "Nueva contrasena (dejar vacio para no cambiar)"
              : "Contrasena *"}
          </label>
          <input
            className="input"
            type="password"
            value={form.password || ""}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </div>
        <div
          className="flex gap-sm"
          style={{ justifyContent: "flex-end", marginTop: 8 }}
        >
          <button className="btn btn-outline" onClick={() => setModal(false)}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={guardar}>
            Guardar
          </button>
        </div>
      </Modal>
    </div>
  );
}
